import { useTranslation } from "react-i18next";
import {
  FileText as FileTextIcon,
  Loader2 as LoaderIcon,
  Music2 as MusicIcon,
  X as CloseIcon,
} from "lucide-react";
import type { OutboundMediaAttachmentType } from "@multi-chat/shared";
import { Button } from "../../../components/ui/button/index.js";
import { cn } from "../../../lib/utils.js";
import { formatHumanFileSize } from "../document-card.js";

export function ComposerAttachmentPreview({
  attachmentType,
  fileName,
  fileSizeBytes,
  previewUrl,
  uploading,
  removeLabel,
  onRemove,
}: {
  attachmentType: OutboundMediaAttachmentType | string;
  fileName: string | null;
  fileSizeBytes: number;
  previewUrl: string | null;
  uploading: boolean;
  removeLabel: string;
  onRemove: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const normalizedType = attachmentType.toUpperCase();
  const sizeLabel = formatHumanFileSize(String(fileSizeBytes));
  const label = fileName?.trim() || t(`inbox.messageType.${normalizedType}`);

  return (
    <div
      className="relative flex min-w-0 items-start gap-2 rounded-xl border border-line/80 bg-surface-muted/70 p-2"
      data-testid="composer-attachment-preview"
      aria-busy={uploading}
    >
      <div className="min-w-0 flex-1">
        {normalizedType === "IMAGE" || normalizedType === "STICKER" ? (
          previewUrl ? (
            <img
              src={previewUrl}
              alt={t("inbox.composer.attachmentPreview")}
              className={cn(
                "max-h-28 max-w-full rounded-lg object-contain",
                normalizedType === "STICKER" && "max-h-24",
              )}
            />
          ) : null
        ) : null}

        {normalizedType === "VIDEO" ? (
          previewUrl ? (
            <video
              src={previewUrl}
              className="max-h-28 max-w-full rounded-lg bg-black/5"
              muted
              playsInline
              preload="metadata"
              aria-label={label}
            />
          ) : null
        ) : null}

        {normalizedType === "AUDIO" ? (
          <div className="flex min-w-0 items-center gap-2 px-1 py-1.5">
            <span className="inline-grid size-9 shrink-0 place-items-center rounded-lg bg-surface text-ink-muted">
              <MusicIcon size={16} aria-hidden="true" />
            </span>
            <div className="min-w-0">
              <p className="m-0 truncate text-[0.8125rem] font-medium text-ink">{label}</p>
              {sizeLabel ? (
                <p className="m-0 text-[0.75rem] text-ink-subtle">{sizeLabel}</p>
              ) : null}
            </div>
          </div>
        ) : null}

        {normalizedType === "DOCUMENT" ? (
          <div className="flex min-w-0 items-center gap-2 px-1 py-1.5">
            <span className="inline-grid size-9 shrink-0 place-items-center rounded-lg bg-surface text-ink-muted">
              <FileTextIcon size={16} aria-hidden="true" />
            </span>
            <div className="min-w-0">
              <p className="m-0 truncate text-[0.8125rem] font-medium text-ink">{label}</p>
              {sizeLabel ? (
                <p className="m-0 text-[0.75rem] text-ink-subtle">{sizeLabel}</p>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>

      <div className="flex shrink-0 items-start gap-1">
        {uploading ? (
          <span className="inline-grid size-8 place-items-center text-ink-subtle" aria-hidden="true">
            <LoaderIcon size={16} className="animate-spin" />
          </span>
        ) : null}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="size-8 shrink-0 rounded-full p-0 text-ink-muted hover:bg-surface hover:text-ink"
          aria-label={removeLabel}
          disabled={uploading}
          onClick={onRemove}
        >
          <CloseIcon size={15} aria-hidden="true" />
        </Button>
      </div>
    </div>
  );
}
